/**
 * Activity Service
 * 
 * Responsible for activity-related API operations (emails and appointments).
 * Follows Single Responsibility Principle.
 */

import { Logger } from '../utils/logger.js';
import { FetchUtils } from '../utils/fetch-utils.js';
import { EmailExtractor } from '../extractors/email-extractor.js';
import { AppointmentExtractor } from '../extractors/appointment-extractor.js';
import { ContactService } from './contact-service.js';

/**
 * Prepares activity data for API requests
 */
class ActivityDataPreparator {
  /**
   * Prepare email data for linking
   */
  static prepareEmailData(emailData, contactIds, selectedLeads, selectedCampaigns, sharedWith = [], actionType = '') {
    const leadId = selectedLeads.length > 0 ? selectedLeads[0] : null;
    const campaignId = selectedCampaigns.length > 0 ? selectedCampaigns[0] : null;

    const contacts = Array.isArray(contactIds) ? contactIds : [contactIds].filter(Boolean);
    const lightweightContacts = ContactService.extractContactData(contacts);

    const preparedData = {
      ...emailData,
      done: true,
      contacts: lightweightContacts,
      lead: leadId,
      campaign: campaignId
    };

    // Add sharedWith field if provided
    if (sharedWith && sharedWith.length > 0) {
      preparedData.sharedWith = sharedWith;
    }

    ActivityDataPreparator.applyActionType(preparedData, actionType);

    return preparedData;
  }

  /**
   * Prepare appointment data for linking
   */
  static prepareAppointmentData(appointmentData, contactIds, selectedLeads, selectedCampaigns, sharedWith = [], actionType = '') {
    const leadId = selectedLeads.length > 0 ? selectedLeads[0] : null;
    const campaignId = selectedCampaigns.length > 0 ? selectedCampaigns[0] : null;

    const contacts = Array.isArray(contactIds) ? contactIds : [contactIds].filter(Boolean);
    const lightweightContacts = ContactService.extractContactData(contacts);

    const preparedData = {
      ...appointmentData,
      done: false,
      contacts: lightweightContacts,
      lead: leadId,
      campaign: campaignId
    };

    // Add sharedWith field if provided
    if (sharedWith && sharedWith.length > 0) {
      preparedData.sharedWith = sharedWith;
    }

    ActivityDataPreparator.applyActionType(preparedData, actionType);

    return preparedData;
  }

  /**
   * Send actionType only when the user picked one.
   * A blank value leaves the CRM mapping from email type in place.
   */
  static applyActionType(preparedData, actionType) {
    if (typeof actionType === 'string' && actionType.trim()) {
      preparedData.actionType = actionType.trim();
    }
  }
}

/**
 * Main Activity Service
 * Follows Single Responsibility Principle
 */
export class ActivityService {
  /**
   * Link email to contacts
   */
  static async linkEmail(contactIds, emailItem, selectedLeads = [], selectedCampaigns = [], sharedWith = [], actionType = '') {
    // Extract email data
    const emailData = await EmailExtractor.extractEmailData(emailItem);
    
    // Prepare final email data
    const finalEmailData = ActivityDataPreparator.prepareEmailData(
      emailData, 
      contactIds, 
      selectedLeads, 
      selectedCampaigns,
      sharedWith,
      actionType
    );

    Logger.debug('Email link request size:', {
      totalContacts: contactIds.length,
      requestBodySize: JSON.stringify(finalEmailData).length
    });

    return FetchUtils.put('/api/email', finalEmailData);
  }
  
  /**
   * Link appointment to contacts
   */
  static async linkAppointment(contactIds, appointmentItem, selectedLeads = [], selectedCampaigns = [], sharedWith = [], actionType = '') {
    // Extract appointment data
    const appointmentData = await AppointmentExtractor.extractAppointmentData(appointmentItem);
    
    // Prepare final appointment data
    const finalAppointmentData = ActivityDataPreparator.prepareAppointmentData(
      appointmentData,
      contactIds,
      selectedLeads,
      selectedCampaigns,
      sharedWith,
      actionType
    );

    Logger.debug('Appointment link request size:', {
      totalContacts: contactIds.length,
      requestBodySize: JSON.stringify(finalAppointmentData).length
    });

    return FetchUtils.put('/api/email', finalAppointmentData);
  }

  /**
   * Activity type options from the tenant metadata list.
   * Returns [] when the list is missing or the request fails, so import can continue.
   */
  static async fetchActionTypes() {
    try {
      const response = await FetchUtils.get('/api/activity/metadata');
      const raw = response?.metadata?.lists?.actionType;
      const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
      return list.filter((item) => item && typeof item.value === 'string' && item.value.trim());
    } catch (error) {
      Logger.debug('Failed to load activity types:', error);
      return [];
    }
  }
}

